/**
 * The 404 view — registered as `config.views.errorViews[404]`.
 *
 * WHY THIS EXISTS
 *
 * Stock Volto's NotFound renders one sentence and a link to `/contact-form`.
 * On these archives that link is a dead end (the route renders, the backend
 * answers 404 for it) and `/contact-us` is private, so the page's only exit
 * goes nowhere. It also renders no `<Helmet>` at all, which is why the live
 * 404 answers with an EMPTY `<title></title>` — the first thing a person sees
 * of it is a blank browser tab.
 *
 * WHAT THIS PAGE IS FOR
 *
 * Not apology, and not recovery-then-exit. A dead URL is the one moment we
 * have someone's attention with a known intent, so the page is built to make
 * them click INTO the archive. Three instruments agree on what that intent is
 * — Search Console (every top query is a document number), the on-site query
 * log (71% of real queries are an identifier or a version-history route), and
 * the shape of the 404 URLs themselves. So the page leads with the document
 * it thinks they meant, and follows it with the thing DOE's own sites cannot
 * offer: the rest of that document's version chain.
 *
 * HONEST STATUS, ALWAYS
 *
 * Core's `Error.jsx` falls back to `errorViews['404']` for ANY status with no
 * registered view, so this component is also what a 500 or a 502 lands on. It
 * reads the real status rather than assuming, tells the truth about it in the
 * copy, and propagates that same code to the SSR response. A page that says
 * "not found" over a 502 sends a crawler to de-index content that is merely
 * having a bad afternoon.
 *
 * WHY THE ENRICHMENT IS CLIENT-SIDE
 *
 * Everything a crawler or a no-JS reader needs — the title, the headings, the
 * search form, the browse link, the sister-archive links — is in the rendered
 * HTML with zero backend calls. The resolver hits, the version chain and the
 * supersession feed are fetched after mount instead.
 *
 * That is not a compromise, it is the point: measured over 30 days, genuine
 * human 404s here are single digits per month and the totals are dominated by
 * scanners (one `/search` burst was a single SQLi scanner in one day). Bots do
 * not run JS, so the expensive version of this page is served only to the
 * handful of real people who reach it, and a scanner costs exactly one render.
 */
import React from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Container } from 'semantic-ui-react';
import Helmet from '@plone/volto/helpers/Helmet/Helmet';
import BodyClass from '@plone/volto/helpers/BodyClass/BodyClass';
import Api from '@plone/volto/helpers/Api/Api';

import { PRESETS } from '../../presets';
import { SITE_CONFIGS } from '../../siteConfig/branding';
import useSiteConfig from '../../helpers/useSiteConfig';
import { renderValue } from '../../helpers/renderValue';
import { HISTORY_SEGMENT } from '../FamilyTimeline/url';
import PageHeader from '../Layout/PageHeader';
import { contactHref } from '../ContactForm/categories';
import {
  MAX_HITS,
  isConfidentMatch,
  presetFor,
  recoverQueries,
  summariseChain,
} from './recoverPath';

/** How many recently-superseded documents to list. */
const CHANGED_LIMIT = 4;

/**
 * The supersession feed. No `portal_type` filter is needed: only document
 * types carry `superseded_date`, so a descending sort puts them first and
 * everything else after. Verified on all three archives, 2026-08-20.
 */
const CHANGED_PATH =
  `/@search?sort_on=superseded_date&sort_order=descending&b_size=${CHANGED_LIMIT}` +
  '&metadata_fields=superseded_date&metadata_fields=docid';

/**
 * How the sister archives are named when linked from another one.
 *
 * NOT `SiteConfig.title` — nnsa's is "Policies & Directives", which on a
 * directives 404 page reads as a duplicate of the archive you are already on.
 * These are the footer's labels, which were written for exactly this job.
 */
const CROSS_LABEL = {
  standards: 'DOE Technical Standards',
  directives: 'DOE Directives',
  nnsa: 'NNSA Policies & Directives',
};

/**
 * Propagate an HTTP status back to the SSR process.
 *
 * Core's `withServerErrorCode` HOC inlined, because that HOC bakes ONE code
 * in at module scope and the code here depends on what actually failed. The
 * empty-keys guard is core's and is load-bearing: it makes the first writer
 * win, so an inner component cannot overwrite a status an outer one set.
 */
const setServerStatus = (props, code) => {
  const { staticContext } = props;
  if (staticContext && Object.keys(staticContext).length === 0) {
    staticContext.error_code = code;
    staticContext.error = props.error;
  }
};

/** `2026-08-05` -> `5 Aug 2026`, in UTC so the date never slips a day. */
const formatDate = (value) => {
  const date = new Date(`${String(value).slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
};

/** A suggestion row: same-archive links route, cross-archive links leave. */
const HitLink = ({ hit, children, className }) =>
  hit.url && hit.url.startsWith('/') ? (
    <Link to={hit.url} className={className}>
      {children}
    </Link>
  ) : (
    <a href={hit.url} className={className}>
      {children}
    </a>
  );

const NotFoundView = (props) => {
  const location = useLocation();
  const history = useHistory();
  const site = useSiteConfig();
  const apierror = useSelector((state) => state.apierror);

  const rawStatus = Number(props.error?.status || apierror?.statusCode) || 404;
  const connectionRefused = !!apierror?.connectionRefused;
  const status = connectionRefused ? 503 : rawStatus;
  const isNotFound = status === 404;
  const isServerError = status >= 500;
  setServerStatus(props, status);

  const dead = location.pathname;
  const { queries, primary } = React.useMemo(
    () => recoverQueries(dead),
    [dead],
  );
  const preset = React.useMemo(
    () => presetFor(dead, site.id, PRESETS),
    [dead, site.id],
  );

  const [term, setTerm] = React.useState('');
  React.useEffect(
    () => setTerm(isNotFound ? primary : ''),
    [primary, isNotFound],
  );

  const [hits, setHits] = React.useState(null);
  const [chain, setChain] = React.useState(null);
  const [changed, setChanged] = React.useState(null);

  // Resolver. Both candidate queries go out together, so asking twice costs
  // one round trip rather than two, and the identifier head wins ties by
  // being merged first.
  const queryKey = queries.join('|');
  React.useEffect(() => {
    if (!isNotFound || !queryKey) return undefined;
    let live = true;
    const api = new Api();
    Promise.all(
      queryKey.split('|').map((q) =>
        api
          .get(`/@suggest?q=${encodeURIComponent(q)}&scope=all`)
          .then((body) => body?.suggestions || [])
          .catch(() => []),
      ),
    ).then((lists) => {
      if (!live) return;
      const seen = new Set();
      const merged = [];
      lists.forEach((list) =>
        list.forEach((hit) => {
          // `docid` is null on fuzzy title matches against attachments and
          // FAQ files. Those are noise on this page: it is answering "which
          // DOCUMENT did you mean", and a document is what has a number.
          if (!hit.docid || seen.has(hit.docid)) return;
          seen.add(hit.docid);
          merged.push(hit);
        }),
      );
      setHits(merged.slice(0, MAX_HITS));
    });
    return () => {
      live = false;
    };
  }, [isNotFound, queryKey]);

  // When the resolver returned an EXACT document-number match, show only the
  // exact matches. Measured on /doe-o-4141e: the resolver answers DOE O 414.1E
  // first and correctly, then DOE N 251.45, DOE G 435.1-1 and DOE N 144.1 —
  // fuzzy neighbours that are fine as fallbacks and actively harmful printed
  // three-deep under a heading that says "Did you mean". Confidence is not a
  // property of the top row; it is a reason to stop listing.
  const confidentHits = React.useMemo(
    () =>
      (hits || []).filter((hit) =>
        queries.some((q) => isConfidentMatch(q, hit.docid)),
      ),
    [hits, queries],
  );
  const confident = confidentHits.length > 0;
  const shownHits = confident ? confidentHits : hits;

  // Same principle one level up. When the dead path matched a preset we have
  // already given an exact, looked-up answer, so a list of fuzzy neighbours
  // underneath it only makes that answer look less certain than it is —
  // /standards-browse resolved to four archived DOE O 252.x directives, none
  // of which the visitor wanted. Confident hits still show: they are answering
  // a different question and they earned the space.
  const showHits =
    isNotFound && shownHits && shownHits.length > 0 && (confident || !preset);

  // Version chain for the first SHOWN hit — one extra call, spent on the one
  // row a reader is most likely to act on. It has to follow the confidence
  // filter, not the raw resolver order: printing a chain that belongs to a
  // different document than the row it sits under is exactly the failure this
  // page cannot make. Cross-archive hits are skipped, because each archive is
  // a separate origin in seamless mode and its API is not reachable from here.
  const top = shownHits && shownHits[0];
  const topUrl = top && top.url && top.url.startsWith('/') ? top.url : null;
  const topDocid = top && top.docid;
  React.useEffect(() => {
    setChain(null);
    if (!topUrl) return undefined;
    let live = true;
    new Api()
      .get(`${topUrl}/@family-timeline`)
      .then((body) => {
        if (live) setChain(summariseChain(topDocid, body));
      })
      .catch(() => {});
    return () => {
      live = false;
    };
  }, [topUrl, topDocid]);

  React.useEffect(() => {
    if (!isNotFound) return undefined;
    let live = true;
    new Api()
      .get(CHANGED_PATH)
      .then((body) => {
        if (!live) return;
        setChanged(
          (body?.items || [])
            .filter((item) => item.docid && item.superseded_date)
            .map((item) => ({
              docid: item.docid,
              title: item.title,
              url: item['@id'],
              date: formatDate(item.superseded_date),
            })),
        );
      })
      .catch(() => {});
    return () => {
      live = false;
    };
  }, [isNotFound]);

  const siblings = Object.keys(SITE_CONFIGS).filter((id) => id !== site.id);

  const title = isNotFound
    ? `Page not found — ${site.title}`
    : isServerError
      ? `Something went wrong — ${site.title}`
      : `Page not available — ${site.title}`;

  const heading = isNotFound
    ? 'No page at this address'
    : isServerError
      ? 'Something went wrong on our side'
      : 'This page could not be loaded';

  const deadPath = (
    <code className="pp-page-head__deadpath" title={dead}>
      {dead}
    </code>
  );

  // The dead path is never the last thing in the sentence. It is an
  // inline-block with 6px of its own padding, so a period set immediately
  // after it lands a visible gap away from the box and reads as a spacing
  // bug; mid-sentence, that same gap is just a word space.
  const lede = isNotFound ? (
    <>
      The address {deadPath} is not part of {site.title}.
    </>
  ) : isServerError ? (
    <>
      {deadPath} exists — this archive just failed to load it. The problem is
      ours, not yours, and it is usually over in moments.
    </>
  ) : (
    <>
      The archive answered {status} for {deadPath} and could not render the
      page.
    </>
  );

  const submit = (event) => {
    event.preventDefault();
    const q = term.trim();
    if (q) history.push(`/search?SearchableText=${encodeURIComponent(q)}`);
  };

  return (
    <Container className="view-wrapper">
      <BodyClass className={isNotFound ? 'view-notfound' : 'view-apierror'} />
      <Helmet>
        <title>{title}</title>
        {/* `noindex` only — deliberately NOT `noindex,nofollow`.
            The 404 status is what actually keeps this page out of the index;
            `noindex` is here because the same component is the 5xx fallback,
            where it is the only signal. `nofollow` was the contradiction: the
            page exists to send a reader onward, and every link on it is a real
            destination. It cost nothing to drop — /documents, /search, /people
            and both sister archives are already in the site-wide nav and
            footer, so nothing here was discoverable only through an error
            page — but a page whose whole purpose is onward links should not
            also tell a crawler to ignore them. */}
        <meta name="robots" content="noindex" />
        <meta
          name="description"
          content={`This address is not part of ${site.title}. Search the archive by document number, or browse it by type, series, status and date.`}
        />
      </Helmet>

      <div className="pp-error">
        <PageHeader title={heading} lede={lede} />

        {isServerError ? (
          <p className="pp-error__caption">
            Reloading is worth trying. If it keeps happening, the archive is
            still reachable through <Link to="/search">search</Link> and{' '}
            <Link to="/documents">the document index</Link>.
          </p>
        ) : null}

        {/* An exact answer, so it goes above the guesses. The consolidation
          kept every deleted browse page's id as its preset id, which makes
          this a lookup rather than a guess — the one thing on this page that
          is certain. */}
        {isNotFound && preset ? (
          <section className="pp-error__section">
            <h2>This page moved</h2>
            <p className="pp-error__moved">
              <Link to={preset.to}>{preset.label}</Link> is now a view of the
              main index rather than a page of its own. Same documents, same
              filters, one address.
            </p>
          </section>
        ) : null}

        {showHits ? (
          <section className="pp-error__section">
            <h2>
              {confident ? 'Did you mean' : 'Closest matches in the archive'}
            </h2>
            <div className="pp-error__didyoumean">
              {shownHits.map((hit, i) => (
                <div className="pp-error__hit" key={hit.docid}>
                  <HitLink hit={hit} className="pp-error__hit-docid">
                    {hit.docid}
                  </HitLink>
                  <span className="pp-error__hit-title">
                    <HitLink hit={hit}>{hit.title}</HitLink>
                  </span>
                  <span className="pp-error__hit-meta">
                    {hit.status ? renderValue(hit.status, 'status') : null}
                    {hit.site && hit.site !== site.id ? (
                      <span className="pp-error__hit-archive">
                        {hit.site_label}
                      </span>
                    ) : null}
                  </span>
                  {/* The differentiator, and the reason this section earns its
                    space: DOE publishes the current text; this archive holds
                    the lineage. Only printed when the family passed the
                    over-merge guard in summariseChain. */}
                  {i === 0 && chain ? (
                    <Link
                      className="pp-error__hit-chain"
                      to={`${hit.url}/${HISTORY_SEGMENT}`}
                    >
                      and {chain.count - 1} earlier{' '}
                      {chain.count === 2 ? 'version' : 'versions'}, {chain.from}{' '}
                      to {chain.to}
                    </Link>
                  ) : null}
                </div>
              ))}
            </div>
          </section>
        ) : null}

        {/* The homepage's own hero search, same `.prc-search` DOM, so the box
          on this page is the box the visitor already knows. Two differences,
          both deliberate: `action`/`method` make it a real GET form that works
          with JavaScript off and tells a crawler where it goes, and it opens
          pre-filled with what the dead URL appears to have been asking for. */}
        <section className="pp-error__section">
          <form
            className="prc-search prc-search--lg pp-error__search"
            role="search"
            action="/search"
            method="get"
            onSubmit={submit}
          >
            <label htmlFor="pp-error-q" className="prc-visually-hidden">
              Search {site.title}
            </label>
            <div className="prc-search__field">
              <div className="prc-search__inputwrap">
                <span className="prc-search__icon" aria-hidden="true">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.75"
                    strokeLinecap="round"
                  >
                    <circle cx="11" cy="11" r="7" />
                    <line x1="16" y1="16" x2="21" y2="21" />
                  </svg>
                </span>
                <input
                  id="pp-error-q"
                  name="SearchableText"
                  type="search"
                  value={term}
                  onChange={(event) => setTerm(event.target.value)}
                  placeholder="Search by document number, title, or keyword…"
                  autoComplete="off"
                />
              </div>
            </div>
            <button type="submit" className="prc-search__btn">
              Search
            </button>
          </form>
          <p className="pp-error__caption">
            Searches the full text inside the documents themselves, across all
            three archives at once — not just their titles. Or{' '}
            <Link to="/documents">browse the index</Link> by type, series,
            status and date.
          </p>
        </section>

        {isNotFound && changed && changed.length ? (
          <section className="pp-error__section">
            <h2>Recently superseded</h2>
            <div className="pp-error__changed">
              {changed.map((item) => (
                <Link
                  className="pp-error__changed-row"
                  to={item.url}
                  key={item.docid}
                >
                  <span className="pp-error__changed-docid">{item.docid}</span>
                  <span className="pp-error__changed-title">{item.title}</span>
                  <span className="pp-error__changed-date">{item.date}</span>
                </Link>
              ))}
            </div>
            <p className="pp-error__caption">
              Each of these still has its own page here, with the version that
              replaced it linked from it.
            </p>
          </section>
        ) : null}

        {/* These are the only inbound links the sister archives have. They are
          in the SSR HTML deliberately — a 404 is not indexed, but it is still
          crawled, and this is a real path between the three properties. */}
        <section className="pp-error__section">
          <h2>Also in this collection</h2>
          <div className="pp-error__siblings">
            {siblings.map((id) => (
              <a
                className="pp-error__sibling"
                href={SITE_CONFIGS[id].baseUrl}
                key={id}
              >
                <span className="pp-error__sibling-name">
                  {CROSS_LABEL[id]}
                </span>
                <span className="pp-error__sibling-note">
                  {SITE_CONFIGS[id].tagline}
                </span>
              </a>
            ))}
          </div>
        </section>

        {/* BL-0096. A dead address that a real person reached by following a
            real link is a defect report we would otherwise never see: the
            edge logs record the URL but not where it was linked from. The
            dead path rides along in the prefill, so the report arrives with
            it already filled in. Below the onward links, not above them —
            most people want the document, not a form.

            `rel="nofollow"` on THIS link does not reverse the page-level
            decision above. That one was about the page's outbound links as a
            class — /documents, /search, /people, the sister archives — which
            are real destinations a crawler should follow. This is the one
            link on the page that is not a destination: it carries the dead
            path as a query parameter, so following it manufactures a unique
            URL per broken address. See contactHref in ContactForm/categories
            and BL-0113. */}
        <p className="pp-error__report">
          Followed a link to get here?{' '}
          <Link
            rel="nofollow"
            to={contactHref({ topic: 'site-bug', path: dead })}
          >
            Tell us where it was
          </Link>
          .
        </p>
      </div>
    </Container>
  );
};

NotFoundView.displayName = 'not-found';

export default NotFoundView;
